import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { todayISO, CONDITION_OPTIONS, PACK_CODE_OPTIONS, CONDITION_DETAIL_OPTIONS, DAMAGE_LOCATION_OPTIONS } from "@/lib/formatters";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateInput } from "@/components/DateInput";
import { ArrowLeft, Plus, Trash2, Save, Send, XCircle, Archive, Camera, Download, Eye, Copy, X, ZoomIn, FileText as FileIcon, Layers, ChevronDown, ChevronUp, PackageCheck } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PdfPreviewDialog } from "@/components/PdfPreview";
import { GoogleActions } from "@/components/GoogleActions";
import { InventoryBasicInfo } from "@/components/inventory/InventoryBasicInfo";
import { InventoryAddresses } from "@/components/inventory/InventoryAddresses";
import { InventoryItemsList } from "@/components/inventory/InventoryItemsList";
import { RelatedDocsList } from "@/components/quote/RelatedDocsList";
import { useLocalDraft } from "@/lib/useLocalDraft";
import { LocalDraftBanner, LocalDraftSavedIndicator } from "@/components/LocalDraftBanner";

const emptyItem = () => ({ id: "", description: "", quantity: 1, pack_code: "", condition: "buono", condition_detail: "", damage_location: "", damage_notes: "", from_quote: false, sort_order: 0 });
const emptyMultiItem = () => ({ description: "", sub_items: [{ description: "", quantity: 1 }] });

export default function InventoryForm() {
  const { id } = useParams();
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const isNew = !id;
  const canCreate = hasPermission("inventories.create");
  const canEditAll = hasPermission("inventories.edit_all");
  const canIssue = hasPermission("inventories.issue");

  const [form, setForm] = useState({
    header_template_id: "", customer_id: "", date: todayISO(),
    pickup_date: "", delivery_date: "",
    pickup_address: "", pickup_city: "", pickup_province: "", pickup_cap: "",
    delivery_type: "consegna",
    delivery_address: "", delivery_city: "", delivery_province: "", delivery_cap: "",
    trip_number: 1, vehicle_type: "", vehicle_plate: "",
    notes: "", remarks: "", items: [emptyItem()], multi_items: [], assigned_to: ""
  });
  const [inv, setInv] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [movers, setMovers] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [thumbnails, setThumbnails] = useState({});
  const [previewAtt, setPreviewAtt] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [relatedDocs, setRelatedDocs] = useState({ inventories: [], proformas: [] });
  const [linkedQuoteDeliveryType, setLinkedQuoteDeliveryType] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [custRes, tmplRes] = await Promise.all([
        api.get("/customers?limit=200"),
        api.get("/header-templates"),
      ]);
      setCustomers(custRes.data.items || []);
      setTemplates(tmplRes.data || []);
      if (canEditAll) {
        const mRes = await api.get("/users");
        setMovers((mRes.data || []).filter(u => u.role === "MOVER" && u.is_active));
      }
      if (id) {
        const { data } = await api.get(`/inventories/${id}`);
        setInv(data);
        setAttachments(data.attachments || []);
        if (data.quote_id) {
          api.get(`/quotes/${data.quote_id}/related-docs`)
            .then(r => setRelatedDocs({ inventories: r.data.inventories || [], proformas: r.data.proformas || [] }))
            .catch(() => {});
          // Carico delivery_type del preventivo per decidere se mostrare il bottone "Crea Riconsegna"
          api.get(`/quotes/${data.quote_id}`)
            .then(r => setLinkedQuoteDeliveryType(r.data?.delivery_type || null))
            .catch(() => setLinkedQuoteDeliveryType(null));
        }
        // Load thumbnails for image attachments
        const imageAtts = (data.attachments || []).filter(a => a.content_type?.startsWith("image/"));
        for (const att of imageAtts) {
          api.get(`/inventories/${id}/attachments/${att.id}/preview`, { responseType: "blob" })
            .then(res => {
              const url = URL.createObjectURL(new Blob([res.data], { type: att.content_type }));
              setThumbnails(prev => ({ ...prev, [att.id]: url }));
            }).catch(() => {});
        }
        setForm({
          header_template_id: data.header_template_id || "",
          customer_id: data.customer_id || "",
          date: data.date || todayISO(),
          pickup_date: data.pickup_date || "",
          delivery_date: data.delivery_date || "",
          pickup_address: data.pickup_address || "",
          pickup_city: data.pickup_city || "",
          pickup_province: data.pickup_province || "",
          pickup_cap: data.pickup_cap || "",
          delivery_type: data.delivery_type || "consegna",
          delivery_address: data.delivery_address || "",
          delivery_city: data.delivery_city || "",
          delivery_province: data.delivery_province || "",
          delivery_cap: data.delivery_cap || "",
          trip_number: data.trip_number || 1,
          vehicle_type: data.vehicle_type || "",
          vehicle_plate: data.vehicle_plate || "",
          notes: data.notes || "",
          remarks: data.remarks || "",
          items: data.items?.length ? data.items.map(it => ({
            ...it,
            pack_code: it.pack_code || "",
            condition_detail: it.condition_detail || "",
            damage_location: it.damage_location || ""
          })) : [emptyItem()],
          multi_items: data.multi_items || [],
          assigned_to: data.assigned_to || ""
        });
      }
    } catch {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  }, [id, canEditAll]);

  useEffect(() => { loadData(); }, [loadData]);

  const isDraft = !inv || inv.status === "DRAFT";
  const isEditable = isDraft;

  // ---- Persistent local draft (offline / network-loss recovery) ----
  const draftKey = isNew ? "inventory:new" : (id ? `inventory:${id}` : null);
  const draft = useLocalDraft({
    key: draftKey,
    form,
    enabled: isEditable && !loading,
    serverUpdatedAt: inv?.updated_at || null,
  });
  const [, forceTick] = useState(0);
  // Re-render every 30s to refresh the relative timestamp in the indicator
  useEffect(() => {
    if (!draft.savedAt) return;
    const t = setInterval(() => forceTick(x => x + 1), 30000);
    return () => clearInterval(t);
  }, [draft.savedAt]);

  const handleRestoreDraft = () => {
    const data = draft.restore();
    if (data) {
      setForm(data);
      toast.success("Bozza locale ripristinata");
    }
  };

  const updateItem = (idx, field, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: val };
    setForm({ ...form, items });
  };
  // Aggiornamento atomico di più campi su un singolo item (utile per "Copia
  // stato al ritiro" che modifica 4 campi insieme).
  const updateItemFields = (idx, partial) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], ...partial };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, emptyItem()] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  // Multi items helpers
  const addMultiItem = () => setForm({ ...form, multi_items: [...form.multi_items, emptyMultiItem()] });
  const removeMultiItem = (idx) => setForm({ ...form, multi_items: form.multi_items.filter((_, i) => i !== idx) });
  const updateMultiItem = (idx, field, val) => { const items = [...form.multi_items]; items[idx] = { ...items[idx], [field]: val }; setForm({ ...form, multi_items: items }); };
  const addSubItem = (miIdx) => { const items = [...form.multi_items]; items[miIdx] = { ...items[miIdx], sub_items: [...items[miIdx].sub_items, { description: "", quantity: 1 }] }; setForm({ ...form, multi_items: items }); };
  const removeSubItem = (miIdx, siIdx) => { const items = [...form.multi_items]; items[miIdx] = { ...items[miIdx], sub_items: items[miIdx].sub_items.filter((_, i) => i !== siIdx) }; setForm({ ...form, multi_items: items }); };
  const updateSubItem = (miIdx, siIdx, field, val) => { const items = [...form.multi_items]; const subs = [...items[miIdx].sub_items]; subs[siIdx] = { ...subs[siIdx], [field]: val }; items[miIdx] = { ...items[miIdx], sub_items: subs }; setForm({ ...form, multi_items: items }); };

  const handleSave = async () => {
    const pickupFields = ['pickup_address', 'pickup_city', 'pickup_province', 'pickup_cap'];
    const deliveryFields = form.delivery_type === "deposito" ? [] : ['delivery_address', 'delivery_city', 'delivery_province', 'delivery_cap'];
    const addrFields = [...pickupFields, ...deliveryFields];
    const missing = addrFields.filter(f => !form[f]?.trim());
    if (missing.length > 0) {
      toast.error("Compilare tutti i campi degli indirizzi (Indirizzo, Città, Provincia, CAP)");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, items: form.items.map((it, i) => ({ ...it, sort_order: i })), trip_number: parseInt(form.trip_number) || 1 };
      if (isNew) {
        const { data } = await api.post("/inventories", payload);
        toast.success("Inventario creato");
        draft.clear(form);
        navigate(`/inventories/${data.id}`, { replace: true });
      } else {
        const { data } = await api.put(`/inventories/${id}`, payload);
        setInv(data);
        toast.success("Inventario aggiornato");
        draft.clear(form);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async (action) => {
    try {
      if (action === "issue") {
        const { data } = await api.post(`/inventories/${id}/issue`);
        setInv(data);
        toast.success("Inventario emesso");
      } else if (action === "version") {
        const { data } = await api.post(`/inventories/${id}/version`);
        toast.success("Nuova versione creata");
        navigate(`/inventories/${data.id}`, { replace: true });
      } else if (action === "cancel") {
        await api.post(`/inventories/${id}/cancel`);
        toast.success("Inventario annullato");
        loadData();
      } else if (action === "archive") {
        await api.post(`/inventories/${id}/archive`);
        toast.success("Inventario archiviato");
        loadData();
      } else if (action === "delete") {
        if (!window.confirm("Eliminare questo inventario?")) return;
        await api.delete(`/inventories/${id}`);
        toast.success("Inventario eliminato");
        navigate("/inventories");
      } else if (action === "pdf") {
        setPdfPreviewUrl(`/inventories/${id}/pdf`);
      } else if (action === "release_inventory") {
        const { data } = await api.post(`/inventories/release-from-inventory/${id}`);
        toast.success("Inventario di Riconsegna creato");
        navigate(`/inventories/${data.id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/inventories/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments(prev => [...prev, data]);
      // Load thumbnail for new image attachment
      if (data.content_type?.startsWith("image/")) {
        api.get(`/inventories/${id}/attachments/${data.id}/preview`, { responseType: "blob" })
          .then(res => {
            const url = URL.createObjectURL(new Blob([res.data], { type: data.content_type }));
            setThumbnails(prev => ({ ...prev, [data.id]: url }));
          }).catch(() => {});
      }
      toast.success("File caricato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDeleteAttachment = async (attId) => {
    try {
      await api.delete(`/inventories/${id}/attachments/${attId}`);
      setAttachments(prev => prev.filter(a => a.id !== attId));
      toast.success("Allegato eliminato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleDownloadAttachment = async (att) => {
    try {
      const response = await api.get(`/inventories/${id}/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore nel download");
    }
  };

  const handlePreviewAttachment = async (att) => {
    try {
      const response = await api.get(`/inventories/${id}/attachments/${att.id}/preview`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: att.content_type });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewAtt(att);
    } catch {
      toast.error("Errore nel caricamento anteprima");
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewAtt(null);
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" /></div>;
  }

  // Visibilità "Crea Inventario Riconsegna" da un pickup specifico:
  // - inventario tipo pickup
  // - preventivo collegato con delivery_type='deposito'
  // - per questo specifico pickup non esiste già una release in DRAFT
  // - permesso di creazione inventari
  const isPickup = inv && (inv.inventory_type === "pickup" || !inv.inventory_type);
  const hasReleaseDraftForThisPickup = (relatedDocs.inventories || []).some(
    (x) => x.inventory_type === "release" && x.status === "DRAFT" && x.original_inventory_id === id
  );
  const existingReleaseForThisPickup = (relatedDocs.inventories || [])
    .filter((x) => x.inventory_type === "release" && x.original_inventory_id === id);
  const canCreateReleaseFromThisPickup = (
    !isNew &&
    isPickup &&
    linkedQuoteDeliveryType === "deposito" &&
    hasPermission("inventories.create") &&
    !hasReleaseDraftForThisPickup &&
    inv?.status !== "CANCELLED" &&
    inv?.status !== "ARCHIVED"
  );

  return (
    <div data-testid="inventory-form-page" className="space-y-4 sm:space-y-6 animate-fade-in pb-24 md:pb-0">
      <div className="md:hidden sticky top-0 z-40 -mx-4 px-4 py-2.5 bg-white/95 backdrop-blur border-b border-zinc-200 flex items-center gap-2">
        <button onClick={() => navigate("/inventories")} className="p-2 -ml-2 hover:bg-zinc-100 rounded-sm" aria-label="Indietro" data-testid="back-btn-mobile">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h1 className="text-base font-bold tracking-tight font-['Outfit'] truncate">{isNew ? "Nuovo Inventario" : (inv?.number || "Bozza")}</h1>
            {inv?.inventory_type === "release" && (
              <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded-sm bg-amber-50 text-amber-700 border border-amber-300">Riconsegna</span>
            )}
          </div>
          {inv && <div className="mt-0.5"><StatusBadge status={inv.status} /></div>}
        </div>
      </div>
      <div className="hidden md:flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/inventories")} className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold tracking-tighter font-['Outfit']">{isNew ? "Nuovo Inventario" : (inv?.number || "Inventario Bozza")}</h1>
              {inv?.inventory_type === "release" && (
                <span data-testid="inv-type-badge-release" className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm bg-amber-50 text-amber-700 border border-amber-300">Riconsegna</span>
              )}
            </div>
            {inv && <StatusBadge status={inv.status} />}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isEditable && <Button onClick={handleSave} disabled={saving} className="bg-[#002FA7] hover:bg-[#002482] rounded-sm" data-testid="save-inventory-btn"><Save className="w-4 h-4 mr-1" /> {saving ? "Salvataggio..." : "Salva"}</Button>}
          {!isNew && canIssue && isDraft && <Button onClick={() => handleAction("issue")} variant="outline" className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="issue-inventory-btn"><Send className="w-4 h-4 mr-1" /> Emetti</Button>}
          {!isNew && canEditAll && inv?.status === "ISSUED" && <Button onClick={() => handleAction("version")} variant="outline" className="rounded-sm" data-testid="version-inventory-btn"><Copy className="w-4 h-4 mr-1" /> Aggiorna ID</Button>}
          {!isNew && isPickup && linkedQuoteDeliveryType === "deposito" && existingReleaseForThisPickup.length > 0 && (() => {
            const last = existingReleaseForThisPickup[existingReleaseForThisPickup.length - 1];
            return (
              <button
                onClick={() => navigate(`/inventories/${last.id}`)}
                data-testid="linked-release-from-pickup-chip"
                title="Apri l'Inventario di Riconsegna collegato a questo ritiro"
                className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-sm border border-amber-300 bg-amber-50/60 hover:bg-amber-100/70 transition text-xs"
              >
                <PackageCheck className="w-3.5 h-3.5 text-amber-700" />
                <span className="text-zinc-700">Riconsegna collegata</span>
                <StatusBadge status={last.status} />
              </button>
            );
          })()}
          {canCreateReleaseFromThisPickup && (
            <Button
              onClick={() => handleAction("release_inventory")}
              variant="outline"
              className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="create-release-inventory-from-pickup-btn"
            >
              <PackageCheck className="w-4 h-4 mr-1" /> Crea Inventario Riconsegna
            </Button>
          )}
          {!isNew && <Button onClick={() => handleAction("pdf")} variant="outline" className="rounded-sm" data-testid="pdf-inventory-btn"><Eye className="w-4 h-4 mr-1" /> Anteprima PDF</Button>}
          {!isNew && hasPermission("google.drive") && <GoogleActions docType="inventory" docId={id} docNumber={inv?.number} />}
          {!isNew && canEditAll && inv?.status !== "CANCELLED" && inv?.status !== "ARCHIVED" && (
            <>
              <Button onClick={() => handleAction("cancel")} variant="outline" className="rounded-sm text-red-600 hover:bg-red-50"><XCircle className="w-4 h-4 mr-1" /> Annulla</Button>
              <Button onClick={() => handleAction("archive")} variant="outline" className="rounded-sm"><Archive className="w-4 h-4 mr-1" /> Archivia</Button>
            </>
          )}
          {!isNew && canEditAll && isDraft && <Button onClick={() => handleAction("delete")} variant="outline" className="rounded-sm text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4 mr-1" /> Elimina</Button>}
        </div>
      </div>

      <div className="md:hidden flex flex-wrap gap-2" data-testid="inv-mobile-secondary-actions">
        {!isNew && canIssue && isDraft && <Button size="sm" onClick={() => handleAction("issue")} variant="outline" className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs h-9" data-testid="issue-inventory-btn-mobile"><Send className="w-3.5 h-3.5 mr-1" /> Emetti</Button>}
        {canCreateReleaseFromThisPickup && (
          <Button
            size="sm"
            onClick={() => handleAction("release_inventory")}
            variant="outline"
            className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50 text-xs h-9"
            data-testid="create-release-inventory-from-pickup-btn-mobile"
          >
            <PackageCheck className="w-3.5 h-3.5 mr-1" /> Riconsegna
          </Button>
        )}
        {!isNew && <Button size="sm" onClick={() => handleAction("pdf")} variant="outline" className="rounded-sm text-xs h-9"><Eye className="w-3.5 h-3.5 mr-1" /> PDF</Button>}
        {!isNew && canEditAll && inv?.status === "ISSUED" && <Button size="sm" onClick={() => handleAction("version")} variant="outline" className="rounded-sm text-xs h-9"><Copy className="w-3.5 h-3.5 mr-1" /> Aggiorna ID</Button>}
        {!isNew && canEditAll && inv?.status !== "CANCELLED" && inv?.status !== "ARCHIVED" && (
          <>
            <Button size="sm" onClick={() => handleAction("cancel")} variant="outline" className="rounded-sm text-red-600 hover:bg-red-50 text-xs h-9"><XCircle className="w-3.5 h-3.5 mr-1" /> Annulla</Button>
            <Button size="sm" onClick={() => handleAction("archive")} variant="outline" className="rounded-sm text-xs h-9"><Archive className="w-3.5 h-3.5 mr-1" /> Archivia</Button>
          </>
        )}
        {!isNew && canEditAll && isDraft && <Button size="sm" onClick={() => handleAction("delete")} variant="outline" className="rounded-sm text-red-600 hover:bg-red-50 text-xs h-9"><Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina</Button>}
      </div>

      {/* Local draft restore banner — visible at mount when a stale offline draft exists */}
      {isEditable && <LocalDraftBanner draft={draft} onRestore={handleRestoreDraft} kind="inv" />}

      {/* Local draft saved indicator — discreet pill under the header */}
      {isEditable && <LocalDraftSavedIndicator draft={draft} kind="inv" />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <InventoryBasicInfo form={form} setForm={setForm} isEditable={isEditable} customers={customers} templates={templates} />

          <InventoryAddresses form={form} setForm={setForm} isEditable={isEditable} />

          <InventoryItemsList form={form} isEditable={isEditable} addItem={addItem} removeItem={removeItem} updateItem={updateItem} updateItemFields={updateItemFields} />

          {/* Multi Items */}
          <div className="card-surface p-4 sm:p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
                <Layers className="w-4 h-4" /> Elementi Multipli
              </h2>
              {isEditable && <Button size="sm" variant="outline" onClick={addMultiItem} className="rounded-sm h-8" data-testid="add-inv-multi-btn"><Plus className="w-3 h-3 mr-1" /> Elemento Multiplo</Button>}
            </div>
            <div className="space-y-3">
              {(form.multi_items || []).map((mi, miIdx) => (
                <InvMultiItemCard key={`inv-mi-${miIdx}`} mi={mi} miIdx={miIdx} isEditable={isEditable}
                  updateMultiItem={updateMultiItem} removeMultiItem={removeMultiItem}
                  addSubItem={addSubItem} removeSubItem={removeSubItem} updateSubItem={updateSubItem} />
              ))}
              {(form.multi_items || []).length === 0 && <p className="text-xs text-zinc-400">Nessun elemento multiplo</p>}
            </div>
          </div>

          {/* Notes */}
          <div className="card-surface p-4 sm:p-5 space-y-4">
            <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Note</h2>
            <textarea data-testid="inv-notes" placeholder="Note..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={!isEditable} className="input-field min-h-[80px] resize-y text-base sm:text-sm" rows={3} />
          </div>

          {/* Remarks / Eccezioni */}
          <div className="card-surface p-4 sm:p-5 space-y-4">
            <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Annotazioni / Eccezioni</h2>
            <textarea data-testid="inv-remarks" placeholder="Annotazioni ed eccezioni relative agli elementi inventariati..." value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} disabled={!isEditable} className="input-field min-h-[80px] resize-y text-base sm:text-sm" rows={3} />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Assignment */}
          {canEditAll && (
            <div className="card-surface p-5 space-y-3">
              <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Assegnazione</h2>
              <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })} disabled={!isEditable}>
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Nessun operatore" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun operatore</SelectItem>
                  {movers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Attachments Gallery */}
          {!isNew && (
            <div className="card-surface p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Allegati</h2>
                {isEditable && (
                  <label className="cursor-pointer">
                    <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={handleUpload} className="hidden" data-testid="upload-attachment-input" />
                    <span className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline font-medium">
                      <Camera className="w-3.5 h-3.5" /> {uploading ? "Caricamento..." : "Carica"}
                    </span>
                  </label>
                )}
              </div>
              {attachments.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {attachments.map(att => {
                    const isImage = att.content_type?.startsWith("image/");
                    return (
                      <div key={att.id} className="group relative border border-zinc-200 rounded-sm overflow-hidden" data-testid={`attachment-${att.id}`}>
                        <button onClick={() => handlePreviewAttachment(att)}
                          className="w-full aspect-square flex items-center justify-center bg-zinc-50 hover:bg-zinc-100 transition-colors cursor-pointer"
                          data-testid={`preview-attachment-${att.id}`}>
                          {isImage ? (
                            <div className="w-full h-full relative">
                              {thumbnails[att.id] ? (
                                <img src={thumbnails[att.id]} alt={att.original_filename}
                                  className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <div className="w-4 h-4 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
                                </div>
                              )}
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-1 text-zinc-400">
                              <FileIcon className="w-8 h-8" />
                              <span className="text-[10px] uppercase tracking-wider">PDF</span>
                            </div>
                          )}
                        </button>
                        <div className="px-2 py-1.5 border-t border-zinc-100 bg-white">
                          <p className="text-[10px] text-zinc-600 truncate" title={att.original_filename}>{att.original_filename}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <button onClick={() => handlePreviewAttachment(att)} className="p-0.5 hover:bg-zinc-100 rounded" title="Anteprima">
                              <Eye className="w-3 h-3 text-zinc-500" />
                            </button>
                            <button onClick={() => handleDownloadAttachment(att)} className="p-0.5 hover:bg-zinc-100 rounded" title="Scarica">
                              <Download className="w-3 h-3 text-zinc-500" />
                            </button>
                            {isEditable && (
                              <button onClick={() => handleDeleteAttachment(att.id)} className="p-0.5 hover:bg-red-50 rounded ml-auto" title="Elimina">
                                <Trash2 className="w-3 h-3 text-red-500" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-zinc-400">Nessun allegato</p>
              )}
            </div>
          )}

          {/* Attachment Preview Lightbox */}
          <Dialog open={!!previewAtt} onOpenChange={(open) => { if (!open) closePreview(); }}>
            <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
              {previewAtt && (
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-white">
                    <span className="text-sm font-medium truncate">{previewAtt.original_filename}</span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleDownloadAttachment(previewAtt)}
                        className="p-1.5 hover:bg-zinc-100 rounded-sm" title="Scarica">
                        <Download className="w-4 h-4 text-zinc-600" />
                      </button>
                      <button onClick={closePreview} className="p-1.5 hover:bg-zinc-100 rounded-sm">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-auto flex items-center justify-center bg-zinc-900/5 min-h-[400px]">
                    {previewUrl && previewAtt.content_type?.startsWith("image/") ? (
                      <img src={previewUrl} alt={previewAtt.original_filename}
                        className="max-w-full max-h-[75vh] object-contain" />
                    ) : previewUrl && previewAtt.content_type === "application/pdf" ? (
                      <iframe src={previewUrl} title={previewAtt.original_filename}
                        className="w-full h-[75vh] border-0" />
                    ) : (
                      <p className="text-sm text-zinc-400">Anteprima non disponibile</p>
                    )}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Related Documents */}
          {!isNew && (() => {
            const filtered = {
              inventories: relatedDocs.inventories.filter(x => x.id !== id),
              proformas: relatedDocs.proformas,
            };
            if (filtered.inventories.length === 0 && filtered.proformas.length === 0) return null;
            return (
              <div className="card-surface p-5 space-y-3" data-testid="related-docs-card">
                <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Documenti Collegati</h2>
                <RelatedDocsList relatedDocs={filtered} navigate={navigate} />
              </div>
            );
          })()}

          {/* Versions */}
          {inv?.versions && inv.versions.length > 1 && (
            <div className="card-surface p-5 space-y-3">
              <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Versioni</h2>
              <div className="space-y-2">
                {inv.versions.map(v => (
                  <button key={v.id} onClick={() => navigate(`/inventories/${v.id}`)}
                    className={`w-full text-left px-3 py-2 rounded-sm text-sm border transition-colors ${v.id === id ? "border-[#002FA7] bg-blue-50/50" : "border-zinc-200 hover:bg-zinc-50"}`}>
                    <span className="font-mono">{v.number || "BOZZA"}</span> <StatusBadge status={v.status} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* PDF Preview Modal */}
      <PdfPreviewDialog
        pdfEndpoint={pdfPreviewUrl}
        open={!!pdfPreviewUrl}
        onClose={() => setPdfPreviewUrl(null)}
        filename={`${inv?.number || "inventario"}.pdf`}
      />

      {/* Mobile sticky save bar */}
      {isEditable && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-zinc-200 px-4 py-3 flex items-center gap-2 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]" data-testid="inv-mobile-save-bar">
          <Button
            onClick={() => navigate("/inventories")}
            variant="outline"
            className="rounded-sm h-11 px-4 flex-shrink-0"
            data-testid="cancel-mobile-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#002FA7] hover:bg-[#002482] rounded-sm h-11 flex-1 text-base font-semibold"
            data-testid="save-inventory-btn-mobile"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Salvataggio..." : "Salva Inventario"}
          </Button>
        </div>
      )}
    </div>
  );
}


function InvMultiItemCard({ mi, miIdx, isEditable, updateMultiItem, removeMultiItem, addSubItem, removeSubItem, updateSubItem }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="border border-[#002FA7]/20 rounded-sm bg-blue-50/30" data-testid={`inv-multi-${miIdx}`}>
      <div className="flex flex-col sm:flex-row gap-2 p-3 sm:items-center">
        <button type="button" onClick={() => setExpanded(!expanded)} className="p-1 hover:bg-white/60 rounded-sm shrink-0 self-start">
          {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </button>
        <Input placeholder="Descrizione gruppo" value={mi.description}
          onChange={(e) => updateMultiItem(miIdx, "description", e.target.value)}
          disabled={!isEditable} className="rounded-sm flex-1 font-medium bg-white" data-testid={`inv-multi-desc-${miIdx}`} />
        {isEditable && (
          <button onClick={() => removeMultiItem(miIdx)} className="p-2 hover:bg-red-50 rounded-sm" data-testid={`inv-multi-delete-${miIdx}`}>
            <Trash2 className="w-3.5 h-3.5 text-red-500" />
          </button>
        )}
      </div>
      {expanded && (
        <div className="px-3 pb-3 pt-1 ml-6 sm:ml-9 space-y-2 border-t border-[#002FA7]/10">
          <div className="flex items-center justify-between pt-2">
            <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-semibold">Sotto-elementi ({(mi.sub_items || []).length})</span>
            {isEditable && (
              <button onClick={() => addSubItem(miIdx)} className="text-xs text-[#002FA7] hover:underline flex items-center gap-0.5" data-testid={`inv-multi-add-sub-${miIdx}`}>
                <Plus className="w-3 h-3" /> Aggiungi
              </button>
            )}
          </div>
          {(mi.sub_items || []).map((si, siIdx) => (
            <div key={`inv-sub-${miIdx}-${siIdx}`} className="flex gap-2 items-center" data-testid={`inv-sub-${miIdx}-${siIdx}`}>
              <span className="text-xs text-zinc-300 w-5 shrink-0 text-right">{siIdx + 1}.</span>
              <Input placeholder="Descrizione elemento" value={si.description}
                onChange={(e) => updateSubItem(miIdx, siIdx, "description", e.target.value)}
                disabled={!isEditable} className="rounded-sm flex-1 h-8 text-sm bg-white" />
              <div className="flex items-center gap-1">
                <Input type="number" min="0" placeholder="Qtà" value={si.quantity}
                  onChange={(e) => updateSubItem(miIdx, siIdx, "quantity", parseInt(e.target.value) || 0)}
                  disabled={!isEditable} className="rounded-sm w-16 h-8 text-sm bg-white" />
                <span className="text-[10px] text-zinc-400">pz</span>
              </div>
              {isEditable && (mi.sub_items || []).length > 1 && (
                <button onClick={() => removeSubItem(miIdx, siIdx)} className="p-1 hover:bg-red-50 rounded-sm">
                  <Trash2 className="w-3 h-3 text-red-400" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
